import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import BoxMui5 from '@mui/material/Box';
import { styled as styledMui5 } from '@mui/material/styles';
import TypographyMui5 from '@mui/material/Typography';
import { scanModeInputAtom } from 'containers/deal/recoil/atoms';
import { PLACEHOLDER_BACKGROUND, PLACEHOLDER_FOREGROUND } from 'data/constants';
import { FC, useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { Loadable, useRecoilValue } from 'recoil';
import { AuctionFromSearch } from 'shared/types/api/getAuctions';
import { GetProgramResponse } from 'shared/types/api/getProgram';
import { getDueAtSignin } from 'shared/utils/program';
import { moneyFormat } from 'utils/functions';
import { getRebatesTotal } from '../../../shared/incentives/getRebatesTotal';
import { MSAPIScanMode, MSISRebate } from '../../../shared/types/marketScan';
import { StyledFontAwesomeIconMui5 } from '../../atoms/FontAwesomeIcon';
import { StyledButtonMui5 } from '../../atoms/StyledButton';

type Props = {
  isProgramValid?: boolean;
  auction: AuctionFromSearch;
  program: Loadable<GetProgramResponse>;
  appliedRebates: MSISRebate[];
  onDiscountClick?: () => void;
  lightMode?: boolean;
};

const MakeModelNameContainer = styledMui5('div')(({ theme }) => {
  const body1 = theme.typography.body1;
  const caption = theme.typography.caption;
  const firstLineHeight = `max(${body1.lineHeight}, ${caption.lineHeight}) * max(${body1.fontSize}, ${caption.fontSize})`;
  const otherLinesHeight = `2 * ${caption.lineHeight} * ${caption.fontSize}`;
  const minHeight = `calc(${firstLineHeight} + ${otherLinesHeight})`;
  return `
    margin: ${theme.spacing(1, 0)};
    min-height: ${minHeight};`;
});

const MakeModelTitle = styledMui5(TypographyMui5)`
  &.MuiTypography-root {
    font-size: 1rem;
    display: inline;
  }`;

type ModelMakeNameProps = {
  make: string;
  model: string;
  style: string;
  year: number;
  msrp?: number;
  payment?: number;
  vin?: string;
};

export const MakeModelName: FC<ModelMakeNameProps> = ({
  make,
  model,
  style,
  year,
  msrp,
  payment,
  vin = null,
}) => (
  <MakeModelNameContainer>
    <div>
      <MakeModelTitle variant='h2'>
        <TypographyMui5 display='inline' component='span' variant='body1'>
          <b>
            {make} {model}
          </b>
        </TypographyMui5>
        &nbsp;
        <TypographyMui5
          display='inline'
          variant='caption'
          color='textSecondary'
        >
          {style} {year}
        </TypographyMui5>
      </MakeModelTitle>
    </div>
    {msrp && (
      <div>
        <TypographyMui5
          display='inline'
          variant='caption'
          color='textSecondary'
        >
          MSRP&nbsp;{moneyFormat(msrp)}
        </TypographyMui5>
      </div>
    )}
    {payment && (
      <BoxMui5>
        <TypographyMui5 display='inline' variant='body2' color='textSecondary'>
          Starts from{' '}
        </TypographyMui5>
        <Payment display='inline' color='primary'>
          {moneyFormat(payment)}
        </Payment>
        &zwj;
        <TypographyMui5 display='inline' variant='body2' color='textSecondary'>
          <b>/mo.</b>
        </TypographyMui5>
      </BoxMui5>
    )}
    {vin && (
      <div>
        <TypographyMui5 display='inline' variant='caption' color='textPrimary'>
          <b>VIN</b>&nbsp;{vin}
        </TypographyMui5>
      </div>
    )}
  </MakeModelNameContainer>
);

const Container = styledMui5('div')`
  display: grid;
  grid-template-columns: 1fr auto;`;

const Content = styledMui5('div')(
  ({ theme }) => `
  &:not(:first-of-type) {
    margin-left: ${theme.spacing(2)};
  }
  margin: ${theme.spacing(1, 0)};`,
);

export const Payment = styledMui5(TypographyMui5)`
  &.MuiTypography-root {
    font-size: 1.5rem;
    font-weight: bold; 
  }`;

export const mileageFormat = (miles: number) => miles?.toLocaleString('en-US');

type OfferPaymentProps = {
  payment: number;
  dueAtSigning: number;
  term: number;
  miles: number;
};
const OfferPayment: FC<OfferPaymentProps> = ({
  dueAtSigning,
  payment,
  term,
  miles,
}) => {
  const scanMode = useRecoilValue(scanModeInputAtom);
  const isFinance = scanMode === MSAPIScanMode.Retail;

  return (
    <>
      <TypographyMui5 display='block' variant='caption' color='primary'>
        {isFinance ? 'FINANCE' : 'LEASE'}&nbsp;PAYMENT
      </TypographyMui5>
      <BoxMui5>
        <Payment display='inline' color='primary'>
          {moneyFormat(payment)}
        </Payment>
        &zwj;
        <TypographyMui5 display='inline' variant='body2' color='textSecondary'>
          <b>/mo.</b>
        </TypographyMui5>
      </BoxMui5>
      <BoxMui5>
        <TypographyMui5
          display='inline'
          variant='caption'
          color='textSecondary'
        >
          +
        </TypographyMui5>
        &zwj;
        <TypographyMui5 display='inline' variant='body2' color='primary'>
          <b>{moneyFormat(dueAtSigning)}</b>
        </TypographyMui5>
        &nbsp;
        <TypographyMui5
          display='inline'
          variant='caption'
          color='textSecondary'
        >
          due&nbsp;at&nbsp;signing
        </TypographyMui5>
      </BoxMui5>
      {isFinance ? (
        <TypographyMui5 display='block' variant='caption' color='textSecondary'>
          {term}&nbsp;{'mo'}
        </TypographyMui5>
      ) : (
        <TypographyMui5 display='block' variant='caption' color='textSecondary'>
          {term}&nbsp;{'mo'}&nbsp;{'/'}&nbsp;{mileageFormat(miles)}&nbsp;
          {'mi'}
        </TypographyMui5>
      )}
    </>
  );
};

type OfferCardDiscountsProps = {
  onDiscountClick?: () => void;
  rebatesDiscount: number;
  programPrice: number;
  msrp: number;
};

const DiscountsButtonLabel = styledMui5('span')`
  display: grid;
  grid-template-rows: repeat(2, auto);
  grid-template-columns: auto;`;

const OfferDiscounts: FC<OfferCardDiscountsProps> = ({
  onDiscountClick,
  rebatesDiscount,
  programPrice,
  msrp,
}) => {
  const totalDiscount = msrp - programPrice + rebatesDiscount;
  return (
    <>
      <StyledButtonMui5
        onClick={() => onDiscountClick?.()}
        variant='outlined'
        color='primary'
      >
        <DiscountsButtonLabel>
          <TypographyMui5
            display='block'
            component='div'
            variant='caption'
            color='inherit'
          >
            DISCOUNTS&nbsp;& INCENTIVES
          </TypographyMui5>
          {totalDiscount > 0 && (
            <TypographyMui5
              display='block'
              component='div'
              variant='body1'
              color='inherit'
              sx={{ color: 'inherit!important' }}
            >
              <b>{moneyFormat(totalDiscount)}</b>
            </TypographyMui5>
          )}
        </DiscountsButtonLabel>
      </StyledButtonMui5>
    </>
  );
};

const InvalidProgramContainer = styledMui5(BoxMui5)(
  ({ theme: t }) => `
  &.MuiBox-root {
    color: ${t.palette.warning.main};
    min-height: 97.083px;
    max-height: 97.083px;
    min-width: 135px;
  }`,
);

const InvalidProgramSizeContainer = styledMui5(BoxMui5)`
  &.MuiBox-root {
    container-type: size;
  }`;

const InvalidProgramText = styledMui5(TypographyMui5)`
  &.MuiTypography-root {
    color: inherit;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 8.2cqw;
    line-height: calc(8.2cqw * 1.4);
  }`;

const InvalidProgramSection: FC<{ isFinance: boolean }> = ({ isFinance }) => (
  <InvalidProgramContainer>
    <InvalidProgramSizeContainer>
      <InvalidProgramText variant='caption' color='inherit'>
        <StyledFontAwesomeIconMui5 icon={faExclamationTriangle} />
        The {isFinance ? 'finance' : 'lease'} is not available under the
        selected conditions
      </InvalidProgramText>
    </InvalidProgramSizeContainer>
  </InvalidProgramContainer>
);

export const OfferCardPrice: FC<Props> = ({
  auction,
  program,
  appliedRebates,
  onDiscountClick,
  isProgramValid = true,
  lightMode = false,
}) => {
  const programState = program.state;
  const programValue = program.valueMaybe();
  const rebatesDiscount = getRebatesTotal(appliedRebates, auction?.msrp);
  const dueAtSigningValue = getDueAtSignin(programValue);

  const [isLoading, setIsLoading] = useState(true);
  const [dueAtSigning, setDueAtSigning] = useState<number>();
  const [term, setTerm] = useState<number>();
  const [miles, setMiles] = useState<number>();
  const [programPrice, setProgramPrice] = useState<number>();
  const [payment, setPayment] = useState<number>();

  const scanMode = useRecoilValue(scanModeInputAtom);
  const isFinance = scanMode === MSAPIScanMode.Retail;

  useEffect(() => {
    if (programState !== 'hasValue') {
      setIsLoading(programState === 'loading');
    }

    if (programState === 'hasValue') {
      setIsLoading(false);
      setPayment(Number(programValue?.Payment));
      setDueAtSigning(Number(dueAtSigningValue));
      setTerm(Number(programValue?.FullDetails.Term));
      if (programValue?.FullDetails?.Lease) {
        setMiles(Number(programValue?.FullDetails.Lease.AnnualMileage));
      }
      setProgramPrice(Number(programValue?.Price));
    }
  }, [programState, programValue, dueAtSigningValue]);

  return (
    <>
      <MakeModelName
        make={auction?.offer?.make?.name}
        model={auction?.offer?.model?.name}
        style={lightMode ? null : auction?.offer?.style_display_name}
        year={lightMode ? null : auction?.offer?.year}
        msrp={lightMode ? null : auction?.msrp}
        payment={lightMode ? payment : null}
        vin={auction?.offer?.vin}
      />

      {!lightMode && (
        <Container>
          <Content>
            {isLoading && <CurrentBidPlaceholder />}
            {!isProgramValid && !isLoading && (
              <InvalidProgramSection isFinance={isFinance} />
            )}
            {isProgramValid && !isLoading && (
              <OfferPayment
                payment={payment}
                dueAtSigning={dueAtSigning}
                term={term}
                miles={miles}
              />
            )}
          </Content>
          <Content>
            <OfferDiscounts
              onDiscountClick={onDiscountClick}
              rebatesDiscount={rebatesDiscount}
              programPrice={programPrice}
              msrp={auction?.msrp}
            />
          </Content>
        </Container>
      )}
    </>
  );
};

const CurrentBidPlaceholder = (props) => (
  <ContentLoader
    speed={2}
    width={135}
    height={93}
    viewBox='0 0 135 93'
    backgroundColor={PLACEHOLDER_BACKGROUND}
    foregroundColor={PLACEHOLDER_FOREGROUND}
    {...props}
  >
    <rect x='0' y='1' rx='2' ry='2' width='88' height='18' />
    <rect x='0' y='21' rx='2' ry='2' width='56' height='30' />
    <rect x='58' y='32' rx='2' ry='2' width='29' height='19' />
    <rect x='-1' y='53' rx='2' ry='2' width='68' height='18' />
    <rect x='69' y='55' rx='2' ry='2' width='65' height='16' />
    <rect x='0' y='73' rx='2' ry='2' width='101' height='19' />
  </ContentLoader>
);

OfferCardPrice.displayName = 'OfferCardPrice';
